import React, { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import ConfirmDialog from "../ConfirmDialog";
import {
  createMatchLevel,
  deleteMatchLevel,
  MATCH_LEVEL_NAME_MAX,
  MatchLevel,
  MatchLevelsStatus,
  reorderMatchLevels,
  updateMatchLevel,
} from "../../services/MatchmakingService";
import { getErrorMessage } from "../../utils/apiResponse";

interface MatchLevelsPanelProps {
  levels: MatchLevel[];
  status: MatchLevelsStatus;
  onChange: (levels: MatchLevel[]) => void;
  onRetry: () => void;
}

// Duplicates are left to the backend: its 409 message is the one shown.
const validateName = (raw: string): string | null => {
  const name = raw.trim();
  if (!name) return "Name is required.";
  if (name.length > MATCH_LEVEL_NAME_MAX) {
    return `At most ${MATCH_LEVEL_NAME_MAX} characters.`;
  }
  return null;
};

// Create/rename suppress the global toast so 400/409 can sit under the input.
const inlineError = (error: any) => {
  if (!error?.response) return "Connection issue. Please try again.";
  if (Number(error.response.status) >= 500) {
    return "Server error. Please try again in a moment.";
  }
  return getErrorMessage(error, "Could not save this level.");
};

const MatchLevelsPanel: React.FC<MatchLevelsPanelProps> = ({
  levels,
  status,
  onChange,
  onRetry,
}) => {
  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<MatchLevel | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const handleAdd = async () => {
    const problem = validateName(newName);
    if (problem) {
      setAddError(problem);
      return;
    }
    setAdding(true);
    try {
      onChange(await createMatchLevel({ name: newName.trim() }));
      setNewName("");
      setAddError(null);
      toast.success("Level added.");
    } catch (error) {
      setAddError(inlineError(error));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (level: MatchLevel) => {
    setEditingId(level.id);
    setEditName(level.name);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const handleRename = async (level: MatchLevel) => {
    if (editName.trim() === level.name) {
      cancelEdit();
      return;
    }
    const problem = validateName(editName);
    if (problem) {
      setEditError(problem);
      return;
    }
    setSavingEdit(true);
    try {
      onChange(await updateMatchLevel(level.id, { name: editName.trim() }));
      cancelEdit();
      toast.success("Level renamed.");
    } catch (error) {
      setEditError(inlineError(error));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    const level = pendingDelete;
    setPendingDelete(null);
    if (!level) return;
    setBusyId(level.id);
    try {
      onChange(await deleteMatchLevel(level.id));
      toast.success("Level deleted.");
    } catch {
      // The interceptor already surfaced the backend message.
    } finally {
      setBusyId(null);
    }
  };

  const handleDrop = async (targetId: string) => {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const previous = levels;
    const next = levels.slice();
    const from = next.findIndex((level) => level.id === sourceId);
    const to = next.findIndex((level) => level.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    // Show the new order straight away; the response is authoritative.
    onChange(next);
    setReordering(true);
    try {
      onChange(await reorderMatchLevels(next.map((level) => level.id)));
    } catch {
      onChange(previous);
    } finally {
      setReordering(false);
    }
  };

  const onlyOneLeft = levels.length <= 1;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.85rem" } }}>
        <Typography sx={{ fontSize: "inherit", mb: 0.5 }}>
          These are the categories players pick when opening a match in the mobile app.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>Renaming a level also updates matches that use it.</li>
          <li>Deleting a level only stops it being offered; existing matches keep their level.</li>
         
        </Box>
      </Alert>

      <Paper
        elevation={0}
        sx={{ border: `1px solid ${COLORS.BorderColor}`, borderRadius: 2, overflow: "hidden" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${COLORS.BorderColor}`,
            bgcolor: "#fafafa",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", flexGrow: 1 }}>
            Levels
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: COLORS.placeHolderColor }}>
            Drag to change the display order
          </Typography>
          {reordering && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>

        {status === "loading" && levels.length === 0 && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {status === "failed" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 2 }}>
            <Typography sx={{ fontSize: "0.88rem", color: COLORS.RED, flexGrow: 1 }}>
              Couldn't load levels
            </Typography>
            <CustomButton label="Retry" variant="outlined" size="small" onClick={onRetry} />
          </Box>
        )}

        {levels.map((level) => {
          const editing = editingId === level.id;
          return (
            <Box
              key={level.id}
              draggable={!editing && !reordering}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                setDragId(level.id);
              }}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                if (overId !== level.id) setOverId(level.id);
              }}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(level.id);
              }}
              sx={{
                display: "flex",
                alignItems: editing ? "flex-start" : "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${COLORS.BorderColor}`,
                bgcolor:
                  overId === level.id && dragId !== level.id
                    ? COLORS.SidebarActiveBg
                    : COLORS.WhiteBackground,
                opacity: dragId === level.id ? 0.5 : 1,
              }}
            >
              <DragIndicatorIcon
                fontSize="small"
                sx={{
                  color: COLORS.placeHolderColor,
                  cursor: editing ? "default" : "grab",
                  mt: editing ? 1 : 0,
                }}
              />

              {editing ? (
                <>
                  <CustomTextField
                    autoFocus
                    minWidth="0px"
                    value={editName}
                    disabled={savingEdit}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      setEditError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(level);
                      if (e.key === "Escape") cancelEdit();
                    }}
                    inputProps={{ maxLength: MATCH_LEVEL_NAME_MAX }}
                    error={!!editError}
                    helperText={editError || `${editName.trim().length}/${MATCH_LEVEL_NAME_MAX}`}
                  />
                  <Tooltip title="Save">
                    <span>
                      <IconButton
                        size="small"
                        disabled={savingEdit}
                        onClick={() => handleRename(level)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={cancelEdit}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Typography sx={{ flexGrow: 1, fontSize: "0.92rem" }}>
                    {level.name}
                  </Typography>
                  {busyId === level.id && <CircularProgress size={16} />}
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => startEdit(level)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={onlyOneLeft ? "At least one level must remain" : "Delete"}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={onlyOneLeft || busyId === level.id}
                        onClick={() => setPendingDelete(level)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 1.5 }}>
          <CustomTextField
            minWidth="0px"
            placeholder="Add level"
            value={newName}
            disabled={adding}
            onChange={(e) => {
              setNewName(e.target.value);
              setAddError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
            inputProps={{ maxLength: MATCH_LEVEL_NAME_MAX }}
            error={!!addError}
            helperText={addError || `${newName.trim().length}/${MATCH_LEVEL_NAME_MAX}`}
          />
          <CustomButton
            label="Add level"
            variant="contained"
            loading={adding}
            onClick={handleAdd}
            sx={{ whiteSpace: "nowrap", height: 40 }}
          />
        </Box>
      </Paper>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete level"
        message={`Delete "${pendingDelete?.name ?? ""}"? Players will no longer be offered it; existing matches keep their level.`}
      />
    </Box>
  );
};

export default MatchLevelsPanel;
